import { fg } from '@atlaskit/platform-feature-flags/fg';

import { SAINFeatureControl } from '../common/utils/sain-feature-control';
import { sha256Hash } from '../sha256-hash';

import { ALLOWED_EXTRA_ATTRIBUTES_KEYS } from './allowed-extra-attributes-keys';
import type { AnalyticsConnectorInfo, commonAttributesType, NLPSearchResultSource } from './types';
import { extractLuminaLinkProducts } from './utils/extractLuminaLinkProducts';
import { extractLuminaUrls } from './utils/extractLuminaUrls';
import { getProductKey } from './utils/getUrlProductKey';

// list from https://atlassian.slack.com/archives/C08VA0N40A2/p1749172242825969?thread_ts=1749088363.059769&cid=C08VA0N40A2
const products1PList = [
	'Assets',
	'Atlas',
	'Confluence',
	'Atlassian Analytics',
	'Atlassian Cloud',
	'Automation for Jira',
	'Bamboo',
	'Bitbucket',
	'Compass',
	'Jira',
	'Jira Product Discovery',
	'Jira Service Management',
	'Jira Software',
	'Jira Work Management',
	'Jira Align',
	'Migration',
	'Loom',
	'Opsgenie',
	'Platform Experiences',
	'Rovo',
	'Statuspage',
	'Trello',
	'webSearch',
	'web-page',
	'Fisheye and Crucible',
	'Crowd',
	'Sourcetree',
	'Unknown',
	'Focus',
	'file-upload',
	'Advanced Roadmaps For Jira',
	'Questions for Confluence',
	'csm',
	'Dewey Support',
	'Dewey JAC Support',
	'Help Aggregator',
];

const noAnswerPhrases = [
	"i'm sorry",
	'i apologize',
	"couldn't find ",
	"couldn't generate",
	'provide more context',
	"don't have enough information",
];

export const isNoAnswerText = (text: string): boolean => {
	const lowerCaseText = text.toLowerCase();
	return noAnswerPhrases.some((phrase) => lowerCaseText.includes(phrase));
};

type SourceProducts = {
	[productName: string]: number;
};

const getSourceProducts = (sources: NLPSearchResultSource[], luminaUrls: string[] = []) => {
	const products: SourceProducts = {};

	sources.forEach((source) => {
		const productName = source.product;
		if (productName) {
			products[productName] = (products[productName] ?? 0) + 1;
		}
	});

	// `luminaUrls` are expected to already deduped
	luminaUrls.forEach((url) => {
		try {
			const productName = getProductKey(url);
			if (productName) {
				products[productName] = (products[productName] ?? 0) + 1;
			}
		} catch {
			return;
		}
	});

	return products;
};

const getLuminaLinkProducts = (luminaLinkProducts: string[] = []): SourceProducts => {
	const products: SourceProducts = {};

	luminaLinkProducts.forEach((productName) => {
		if (productName) {
			products[productName] = (products[productName] ?? 0) + 1;
		}
	});

	return products;
};

const sanitizeSources = (sources: NLPSearchResultSource[]): { id: string; type: string }[] =>
	sources.map((source) => ({
		id: source.ari ?? source.id,
		type: source.type,
	}));

/**
 * Extracts connector info from sources for frontend analytics events. Each source's
 * `connectorInfo` is mapped to the analytics schema (`sourceProduct` -> `source_product`,
 * `searchRetrievalType` -> `search_retrieval_type`), omitting any fields that are absent.
 * Sources without any connector info are skipped, and `undefined` is returned when there is
 * nothing to report so the attribute is left off the event entirely.
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const extractConnectorInfo = (
	sources: NLPSearchResultSource[],
	isReadingAids: boolean | undefined,
): AnalyticsConnectorInfo | undefined => {
	const isConnectorInfoAnalyticsEnabled = isReadingAids
		? fg('kd_definitions_connector_info_analytics')
		: fg('kd_sain_connector_info_analytics');

	if (!isConnectorInfoAnalyticsEnabled) {
		return undefined;
	}

	const connectorInfo: AnalyticsConnectorInfo = sources.flatMap((source) => {
		const { provider_id, connector_type, searchRetrievalType, sourceProduct } =
			source.connectorInfo ?? {};

		if (!provider_id && !connector_type && !searchRetrievalType && !sourceProduct) {
			return [];
		}

		return [
			{
				...(sourceProduct && { source_product: sourceProduct }),
				...(connector_type && { connector_type }),
				...(provider_id && { provider_id }),
				...(searchRetrievalType && { search_retrieval_type: searchRetrievalType }),
			},
		];
	});

	return connectorInfo.length ? connectorInfo : undefined;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const filter3PSources = (sources: NLPSearchResultSource[]): NLPSearchResultSource[] =>
	sources.filter((source) =>
		source.product
			? !products1PList.includes(source.product) && !source.product.includes('Plugin')
			: false,
	);

const filterLumina3PUrls = (urls: string[]): string[] =>
	urls.filter((url) => {
		try {
			const product = getProductKey(url);
			return isSourceProduct3P(product);
		} catch {
			return false;
		}
	});

/**
 * Returns true if the given product string represents a third-party (non-Atlassian) source.
 * Uses the same logic as filter3PSources.
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isSourceProduct3P = (product: string | null | undefined): boolean =>
	product ? !products1PList.includes(product) && !product.includes('Plugin') : false;

type ExtraAttributes = {
	[key: string]: any;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const sanitizeExtraAttributes: (extraAttributes: ExtraAttributes) => ExtraAttributes = (
	extraAttributes: ExtraAttributes,
) => {
	if (!fg('kd_fe_sanitize_analytics')) {
		return extraAttributes;
	}

	const sanitizedAttributes: ExtraAttributes = {};
	// Sanitize everything except COMMON_ALLOWED_KEYS
	for (const key in extraAttributes) {
		if (ALLOWED_EXTRA_ATTRIBUTES_KEYS.has(key)) {
			sanitizedAttributes[key] = extraAttributes[key];
		} else {
			// replacing with text because the value has type `any`
			sanitizedAttributes[key] = '[sanitized]';
		}
	}

	return sanitizedAttributes;
};

/**
 * Keys produced by `filterCommonAttributes` (including optional and feature-gated fields). Keep in
 * sync when adding new attributes there — `SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST` is
 * derived from this list plus `ALLOWED_EXTRA_ATTRIBUTES_KEYS`, `location`, and `source`.
 */
const FILTER_COMMON_ATTRIBUTES_OUTPUT_KEYS: readonly string[] = [
	'searchSessionId',
	'queryHash',
	'baseQueryHash',
	'wordCount',
	'queryLength',
	'answerHash',
	'appliedFilters',
	'answerFormat',
	'answerLength',
	'answerWordCount',
	'answerCardType',
	'followUpsCount',
	'followUpsLength',
	'has3PSources',
	'sources',
	'sourceProducts',
	'apiSource',
	'brand',
	'sessionId',
	'isHello',
	'sourceProduct',
	'isReadingAids',
	'queryIntent',
	'rovoJourneyId',
	'sourcesCount',
	'sources3P',
	'sources3PCount',
	'hasSources',
	'has1PSources',
	'containsNoAnswerPhrase',
	'containsLuminaContent',
	'luminaContentTypes',
	'jiraNlToJqlCardVariant',
	'connectorInfo',
	'luminaLinkProducts',
];

/**
 * Only these `attributes` keys may be passed through Search AI analytics context. Everything else
 * is dropped to prevent UGC/PII from reaching linking-platform analytics context.
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST: Set<string> = new Set<string>([
	'location',
	'source',
	...FILTER_COMMON_ATTRIBUTES_OUTPUT_KEYS,
	...ALLOWED_EXTRA_ATTRIBUTES_KEYS,
]);

export type SearchAIAnalyticsContextDataInput = {
	attributes?: Record<string, unknown>;
	location: string;
};

/** Output of {@link sanitizeSearchAIAnalyticsContextData}; `attributes` is always a defined object (possibly empty). */
export type SearchAIAnalyticsContextSanitized = {
	attributes: Record<string, unknown>;
	location: string;
};

/**
 * Single allowlist pass for Search AI analytics context: keeps only keys in
 * {@link SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST}. Use this (via {@link SearchAIAnalyticsContext}) before
 * values reach `AnalyticsContext`.
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const sanitizeSearchAIAnalyticsContextData = (
	data: SearchAIAnalyticsContextDataInput,
): SearchAIAnalyticsContextSanitized => {
	const location = data.location;
	const raw: Record<string, unknown> = { ...data.attributes };
	if (raw.location === undefined) {
		raw.location = location;
	}

	const attributes: Record<string, unknown> = {};
	for (const key of Object.keys(raw)) {
		if (SEARCH_AI_ANALYTICS_CONTEXT_ATTRIBUTE_ALLOWLIST.has(key)) {
			attributes[key] = raw[key];
		}
	}

	return { attributes, location };
};

// New instrumentation attributes must be added to this function to be included in all the events
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const filterCommonAttributes: (props: commonAttributesType) => {
	searchSessionId: string;
	queryHash: string;
	baseQueryHash: string;
	wordCount: number;
	queryLength: number;
	answerHash: string;
	appliedFilters: import('./types').parsedFiltersType | undefined;
	answerFormat: import('./nlp-search-result-format').NLPSearchResultFormat | null;
	answerLength: number;
	answerWordCount: number;
	answerCardType: import('../constants').AnswerCardType;
	followUpsCount: number;
	followUpsLength: number[];
	has3PSources: boolean;
	sources: {
		id: string;
		type: string;
	}[];
	sourcesCount?: number;
	sources3P?: {
		id: string;
		type: string;
	}[];
	sources3PCount?: number;
	hasSources?: boolean;
	has1PSources?: boolean;
	containsNoAnswerPhrase?: boolean;
	sourceProducts: SourceProducts;
	apiSource: 'assistance-service' | 'cc-search-nlp' | 'xp-knowledge-discovery';
	brand: import('..').AiBrand;
	sessionId: string;
	rovoJourneyId?: string;
	isHello: boolean;
	containsLuminaContent?: boolean;
	luminaContentTypes?: string[];
	connectorInfo?: AnalyticsConnectorInfo;
	luminaLinkProducts?: SourceProducts;
} = (props: commonAttributesType) => {
	const {
		appliedFilters,
		answerFormat,
		answerLength,
		answerWordCount,
		answerCardType,
		followUps,
		sources,
		extraAttributes,
		searchSessionId,
		queryHash,
		baseQueryHash,
		query,
		answerString,
		apiSource,
		brand,
		sourceProduct,
		isReadingAids,
		queryIntent,
		sessionId,
		rovoJourneyId,
		isHello,
		containsLuminaContent,
		luminaContentTypes,
		luminaContentMap,
	} = props;

	const filtered3P = filter3PSources(sources);

	const luminaUrls = SAINFeatureControl.isSAINSourceProductUpdateEnabled()
		? extractLuminaUrls(luminaContentMap ?? {})
		: [];

	// Dedupe lumina URLs against the existing sources so we don't double-count URLs
	// that are already represented in `sources` (which includes all 3P sources).
	const sourceUrls = new Set(sources.map((source) => source.url));
	const dedupedLuminaUrls = luminaUrls.filter((url) => !sourceUrls.has(url));

	const filteredLumina3P = filterLumina3PUrls(dedupedLuminaUrls);

	return {
		searchSessionId,
		queryHash,
		baseQueryHash,
		wordCount: (query.match(/\w+/g) || []).length,
		queryLength: query.length,
		answerHash: sha256Hash(answerString),
		appliedFilters,
		answerFormat,
		answerLength,
		answerWordCount,
		answerCardType,
		followUpsCount: followUps.length,
		followUpsLength: followUps.map((followUp) => followUp.length),
		has3PSources: filtered3P.length > 0 || filteredLumina3P.length > 0,
		sources: sanitizeSources(sources),
		sourceProducts: getSourceProducts(sources, dedupedLuminaUrls),
		apiSource,
		brand,
		...(sourceProduct !== undefined && { sourceProduct }),
		...(isReadingAids !== undefined && { isReadingAids }),
		...(queryIntent !== undefined && { queryIntent }),
		...(containsLuminaContent !== undefined && { containsLuminaContent }),
		...(luminaContentTypes !== undefined && { luminaContentTypes }),
		...(fg('kd_consolidate_sources_analytics') && {
			sourcesCount: sources.length + dedupedLuminaUrls.length,
			sources3P: sanitizeSources(filtered3P),
			sources3PCount: filtered3P.length + filteredLumina3P.length,
		}),
		...(fg('kd_send_no_answer_attributes') && {
			hasSources: sources.length > 0,
			has1PSources: sources.length > filtered3P.length,
			containsNoAnswerPhrase: isNoAnswerText(answerString),
		}),
		...(() => {
			const connectorInfo = extractConnectorInfo(sources, isReadingAids);
			return connectorInfo !== undefined && { connectorInfo };
		})(),
		...(fg('kd_sain_lumina_link_product_temp') && {
			luminaLinkProducts: getLuminaLinkProducts(extractLuminaLinkProducts(luminaContentMap ?? {})),
		}),
		sessionId: sessionId,
		...(rovoJourneyId !== undefined &&
			fg('rovo_search_chat_convergence_instrumentation') && { rovoJourneyId }),
		isHello,
		...sanitizeExtraAttributes(extraAttributes),
	};
};
